// The free aggregate — provider #1 and the universal fallback (D-022).
// Composes free/public sources per asset class: crypto via Binance
// (live, free), everything else via Yahoo's public endpoint (delayed,
// best-effort). Sources fail over to each other; per-source health is
// tracked for the Settings status line. Honest label lives in the UI:
// "Delayed public data — not guaranteed live."

import type { DataProvider, CandleResult, ClosesResult } from "./types.js";
import { yahooCandles, yahooCloses } from "./yahoo-source.js";
import { binanceCandles, binanceSymbol, binanceInterval } from "./binance-source.js";
import { coinbaseCandles, coinbaseProduct, coinbaseGranularity } from "./coinbase-source.js";
import { stooqCandles, stooqSymbol, stooqInterval } from "./stooq-source.js";

export interface SourceHealth {
  ok: boolean;
  at: number; // epoch ms of last attempt
}

/** Module-level health map — cheap, per-context, good enough for a status dot. */
export const sourceHealth: Record<string, SourceHealth> = {};

function recordHealth(source: string, ok: boolean): void {
  sourceHealth[source] = { ok, at: Date.now() };
}

async function withHealth<T>(source: string, run: () => Promise<T>): Promise<T> {
  try {
    const result = await run();
    recordHealth(source, true);
    return result;
  } catch (error) {
    recordHealth(source, false);
    throw error;
  }
}

/** Source order per symbol/interval (D-022: aggregate several free
 * sources so one outage never blanks a chart):
 *   crypto      → Binance (live) → Coinbase (live) → Yahoo (delayed)
 *   everything  → Yahoo q1 → Yahoo q2 (inside yahoo-source) → Stooq
 *                 (daily/weekly only — the one independent free source
 *                 covering futures/stocks/indices) */
async function aggregateCandles(symbol: string, range: string, interval: string): Promise<CandleResult> {
  if (binanceSymbol(symbol) && binanceInterval(interval)) {
    try {
      return await withHealth("binance", () => binanceCandles(symbol, range, interval));
    } catch {
      // next crypto source
    }
    if (coinbaseProduct(symbol) && coinbaseGranularity(interval)) {
      try {
        return await withHealth("coinbase", () => coinbaseCandles(symbol, range, interval));
      } catch {
        // fall through to Yahoo
      }
    }
  }
  try {
    return await withHealth("yahoo", () => yahooCandles(symbol, range, interval));
  } catch (error) {
    if (stooqSymbol(symbol) && stooqInterval(interval)) {
      return await withHealth("stooq", () => stooqCandles(symbol, range, interval));
    }
    throw error;
  }
}

export const freeProvider: DataProvider = {
  id: "free",
  displayName: "Free public data",
  assetClasses: ["Stocks", "Futures", "Forex", "Crypto", "Indices"],
  realtime: false, // crypto happens to be live, but we promise only delayed
  setup: null,
  candles: aggregateCandles,
  async closes(symbol, range, interval): Promise<ClosesResult> {
    // Same source ladder as candles; closes are just the projection.
    try {
      const { candles, currentPrice } = await aggregateCandles(symbol, range, interval);
      if (candles.length) return { closes: candles.map((c) => c.close), regularMarketPrice: currentPrice };
    } catch {
      // fall through to the closes-optimized Yahoo path as a last resort
    }
    return await withHealth("yahoo", () => yahooCloses(symbol, range, interval));
  },
};
